import React, { Fragment, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Menu, TriangleAlert } from 'lucide';

// MUI
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

// common
import Icon from 'common/components/Icon';
import {
  BowlAndChopstick,
  DishIcon,
  DollarIcon,
  FoodMenuIcon,
  FoodTraceabilityIcon,
  LogoImage,
  NutritionIcon,
  ProfileIcon,
  SchoolIcon,
} from 'svg';

// screen
import useGetRole from 'common/hook/useGetRole';
import { showManagingOrg } from 'config/brand';
import BalanceCostDialog from 'modules/map/components/balance-cost/BalanceCostDialog';
import DailyConsolidateInvoiceDialog from 'modules/map/components/daily-consolidate-invoice/DailyConsolidateInvoiceDialog';
import DailyDishDialog from 'modules/map/components/daily-dish/DailyDishDialog';
import FoodTraceabilityDialog from 'modules/map/components/food-traceability/FoodTraceabilityDialog';
import HVSInfo from 'modules/map/components/HVSInfo';
import { focusHvs } from 'modules/map/helpers/focusHvs';
import ListSchool from 'modules/map/components/ListSchool';
import NutritionMenuDialog from 'modules/map/components/nutrition-menu/NutritionMenuDialog';
import SchoolInfoBox from 'modules/map/components/SchoolInfoBox';
import TeacherMenu from 'modules/map/components/teacher-menu/TeacherMenu';
import { ClientModel, FilterModel, HVSInfoModel, MapDialogKey } from 'modules/map/types';

interface Props {
  isClient: boolean;
  isGuest: boolean;
  dataHVS?: HVSInfoModel;
  map: vietmapgl.Map | null;
  data: ClientModel[];
  filterData: ClientModel[];
  filter: FilterModel;
  setFilter: React.Dispatch<React.SetStateAction<FilterModel>>;
  focusData?: ClientModel;
  setFocusData: React.Dispatch<React.SetStateAction<ClientModel | undefined>>;
  openHVS?: boolean;
  setOpenHVS: React.Dispatch<React.SetStateAction<boolean>>;
  foodTraceabilityRestore?: { page?: number; rowsPerPage?: number };
}
function ControllerButton(props: Props) {
  const {
    data,
    filterData,
    filter,
    setFilter,
    focusData,
    setFocusData,
    map,
    dataHVS,
    setOpenHVS,
    openHVS,
    isClient,
    isGuest,
    foodTraceabilityRestore,
  } = props;

  const {
    customerProfile,
    abridgedMenu,
    nutriMenu,
    menuBalance,
    dailyInvoice,
    teacherMenu,
    foodTraceability,
  } = useGetRole(focusData);

  const dataSchool = data.length === 1 ? data[0] : focusData;
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('tablet'));
  const [open, setOpen] = useState(isTablet);
  const [collapse, setCollapse] = useState(true);

  const items = useMemo(
    () =>
      [
        {
          id: 'schoolInfo' as const,
          icon: <ProfileIcon />,
          label: 'map.schoolInfo',
          enabled: customerProfile,
        },
        {
          id: 'dailyDish' as const,
          icon: <DishIcon />,
          label: 'map.dailyDish',
          enabled: abridgedMenu,
          component: (id: MapDialogKey) => (
            <DailyDishDialog
              clientData={focusData!}
              open={focusData?.activeKey === id}
              onClose={() =>
                setFocusData((old) => (old ? { ...old, activeKey: undefined } : undefined))
              }
            />
          ),
        },
        {
          id: 'nutritionMenu' as const,
          icon: <NutritionIcon />,
          label: 'map.nutritionMenu',
          enabled: nutriMenu,
          component: (id: MapDialogKey) => (
            <NutritionMenuDialog
              clientData={focusData!}
              open={focusData?.activeKey === id}
              onClose={() =>
                setFocusData((old) => (old ? { ...old, activeKey: undefined } : undefined))
              }
            />
          ),
        },
        {
          id: 'teacherNutritionMenu' as const,
          icon: <BowlAndChopstick />,
          label: 'map.teacherNutritionMenu',
          enabled: teacherMenu,
          component: (id: MapDialogKey) => (
            <TeacherMenu
              clientData={focusData!}
              open={focusData?.activeKey === id}
              onClose={() =>
                setFocusData((old) => (old ? { ...old, activeKey: undefined } : undefined))
              }
            />
          ),
        },
        {
          id: 'dailyInvoice' as const,
          icon: <FoodMenuIcon />,
          label: 'map.dailyInvoice',
          enabled: dailyInvoice,
          component: (id: MapDialogKey) => (
            <DailyConsolidateInvoiceDialog
              clientData={focusData!}
              open={focusData?.activeKey === id}
              onClose={() =>
                setFocusData((old) => (old ? { ...old, activeKey: undefined } : undefined))
              }
            />
          ),
        },
        {
          id: 'balanceMenu' as const,
          icon: <DollarIcon />,
          label: 'map.balanceMenu',
          enabled: menuBalance,
          component: (id: MapDialogKey) => (
            <BalanceCostDialog
              clientData={focusData!}
              open={focusData?.activeKey === id}
              onClose={() =>
                setFocusData((old) => (old ? { ...old, activeKey: undefined } : undefined))
              }
            />
          ),
        },
        {
          id: 'foodTraceability' as const,
          icon: <FoodTraceabilityIcon />,
          label: 'map.foodTraceability.title',
          enabled: foodTraceability,
          component: (id: MapDialogKey) => (
            <FoodTraceabilityDialog
              clientData={focusData!}
              open={focusData?.activeKey === id}
              onClose={() =>
                setFocusData((old) => (old ? { ...old, activeKey: undefined } : undefined))
              }
              initialPage={foodTraceabilityRestore?.page}
              initialRowsPerPage={foodTraceabilityRestore?.rowsPerPage}
            />
          ),
        },
      ] as const,
    [
      customerProfile,
      abridgedMenu,
      nutriMenu,
      teacherMenu,
      dailyInvoice,
      menuBalance,
      foodTraceability,
      focusData,
      setFocusData,
      foodTraceabilityRestore,
    ]
  );
  const enabledItems = items.filter((v) => v.enabled);

  return (
    <>
      {/* Dialogs render whenever focusData is set, regardless of map readiness */}
      {focusData &&
        enabledItems.map((item) =>
          'component' in item && item.component ? (
            <Fragment key={item.id}>{item.component(item.id)}</Fragment>
          ) : null
        )}

      {/* Visible UI waits for map to initialize */}
      {!map ? null : (
      <>
      <Box className="absolute right-3 top-3 flex gap-2 md:gap-4">
        {showManagingOrg && dataHVS && (
          <Card
            sx={{
              height: 52,
              width: 52,
              borderRadius: 3,
              cursor: 'pointer',
            }}
            onClick={() => focusHvs(map, dataHVS, setOpenHVS)}
          >
            <LogoImage style={{ width: '100%', height: '100%' }} />
          </Card>
        )}
        {dataSchool ? (
          <Card
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 52,
              borderRadius: 3,
              p: 1,
              cursor: 'pointer',
            }}
            onClick={() => {
              setFocusData(dataSchool ?? undefined);
              map?.flyTo({
                center: [dataSchool.lon, dataSchool.lat],
                zoom: 14,
                duration: 1000,
              });
            }}
            className="max-w-[240px] tablet:max-w-none"
          >
            <SchoolIcon type={dataSchool.type} style={{ width: 'auto', height: '100%' }} />
            <Box ml={1}>
              <Typography variant="subtitle2" mb={0.25}>
                {dataSchool.name}
              </Typography>
              <Typography variant="caption">
                {[dataSchool.wardName, dataSchool.cityName].join(', ')}
              </Typography>
            </Box>
          </Card>
        ) : (
          <Card
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 52,
              borderRadius: 3,
              p: 1,
              cursor: 'pointer',
            }}
            onClick={() => {
              setOpen(true);
            }}
          >
            <Box component="span" sx={{ display: 'inline-flex', color: 'error.main' }}>
              <Icon icon={TriangleAlert} size={32} />
            </Box>
            <Typography variant="body2">
              <FormattedMessage id="map.noCustomer" />
            </Typography>
          </Card>
        )}
      </Box>
      {focusData && (
        <Card
          sx={{
            borderRadius: 3,
            p: 0.5,
            width: 62,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            position: 'absolute',
            top: 86,
            right: 12,
            // Màn thấp: danh sách chức năng cuộn trong cột, không bị cắt mất nút phía dưới.
            maxHeight: 'calc(100% - 100px)',
            overflowY: 'auto',
          }}
        >
          <Tooltip title={<FormattedMessage id="map.features" />} placement="left">
            <ButtonBase
              aria-label="Chức năng"
              sx={{ borderRadius: 3, color: 'error.main' }}
              onClick={() => setCollapse((old) => !old)}
            >
              <Icon icon={Menu} />
            </ButtonBase>
          </Tooltip>
          <Collapse in={collapse}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mt: 2,
              }}
            >
              {enabledItems.map((item) => (
                <Tooltip
                  key={item.id}
                  title={<FormattedMessage id={item.label} />}
                  placement="left"
                >
                  <Button
                    color={focusData.activeKey === item.id ? 'primary' : 'inherit'}
                    sx={{
                      borderRadius: 3,
                      height: 50,
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: focusData.activeKey === item.id ? 'primary.main' : 'divider',
                    }}
                    onClick={() => {
                      setFocusData((old) =>
                        old ? { ...old, activeKey: item.id } : undefined
                      );
                    }}
                  >
                    {item.icon}
                  </Button>
                </Tooltip>
              ))}
            </Box>
          </Collapse>
        </Card>
      )}

      <Box className="absolute left-4 top-4 bottom-4">
        {!isClient && !isGuest && (
          <ListSchool
            map={map}
            open={open}
            setOpen={setOpen}
            filter={filter}
            setFilter={setFilter}
            filterData={filterData}
            data={data}
            focusData={focusData}
            setFocusData={setFocusData}
          />
        )}
        {showManagingOrg && dataHVS && (
          <HVSInfo data={dataHVS} open={openHVS} onClose={() => setOpenHVS(false)} />
        )}
        <SchoolInfoBox
          data={focusData}
          open={!!focusData}
          onClose={() => setFocusData(undefined)}
        />
      </Box>
      </>
      )}
    </>
  );
}

export default ControllerButton;
